import type { AppDefinition } from '@/types/app';
import type { StyleBrief, PatternId } from './planTypes';

const RADIUS_SCALE = {
  sharp: { sm: 3, md: 6, lg: 10, full: 999 },
  soft:  { sm: 8, md: 14, lg: 20, full: 999 },
  round: { sm: 12, md: 20, lg: 28, full: 999 },
} as const;

// Premium-leaning: even 'comfortable' keeps generous margins (baseTheme md is 16). Spacing must
// read premium — never cramped.
const SPACING_SCALE = {
  tight:       { xs: 4, sm: 8, md: 14, lg: 20, xl: 26 },
  comfortable: { xs: 4, sm: 10, md: 16, lg: 24, xl: 32 },
  airy:        { xs: 6, sm: 14, md: 22, lg: 30, xl: 40 },
} as const;

const H1_SIZE = { restrained: 28, bold: 34, hero: 44 } as const;

const PATTERN_PHRASE: Record<PatternId, string> = {
  'two-tone-headings': 'two-tone headings (a dimmed secondary segment, e.g. "$214" + muted ".82")',
  'floating-tabbar': 'a floating glass bottom tab bar',
  'icon-chips': 'soft-tint category chips with a leading icon',
  'segmented-range': 'a segmented pill control for range/filter switches',
  'gradient-chart': 'a smooth gradient-area line chart',
  'peek-carousel': 'a peek carousel (next item bleeding in)',
  'glass-overlay': 'a glass overlay pill on imagery',
  'paired-stats': 'paired left/right stat rows',
};

/** Deterministically fold a Design DNA brief into the generated theme: palette + radius + spacing
 *  + headline scale. Merges onto whatever theme the definition already has. */
export function applyStyleBrief(def: AppDefinition, brief: StyleBrief): AppDefinition {
  const theme = (def.theme ?? {}) as AppDefinition['theme'];
  const colors = { ...((theme?.colors as Record<string, unknown>) ?? {}) };
  const p = brief.palette;
  const nextColors = { ...colors, primary: p.primary, secondary: p.accent ?? p.primary, background: p.background, text: p.text };
  const h1 = { ...(theme?.typography?.h1 ?? { fontSize: 34, fontWeight: '800' }), fontSize: H1_SIZE[brief.typeScale], fontWeight: brief.typeScale === 'restrained' ? '700' : '800' };
  return {
    ...def,
    theme: {
      ...(theme ?? {}),
      colors: nextColors as never,
      borderRadius: { ...(theme?.borderRadius ?? {}), ...RADIUS_SCALE[brief.radius] } as never,
      spacing: { ...(theme?.spacing ?? {}), ...SPACING_SCALE[brief.density] } as never,
      typography: { ...(theme?.typography ?? {}), h1 } as never,
    } as never,
  };
}

/** One build directive telling the generator which premium patterns to use. Empty when none. */
export function patternDirectives(patterns: PatternId[]): string {
  if (!patterns.length) return '';
  return `Use these premium UI patterns where appropriate: ${patterns.map((p) => PATTERN_PHRASE[p]).join('; ')}.`;
}
